import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { apiService, LoginRequest } from '../services/api';
import { useNotifications } from './NotificationSystem';
import { handleApiError, validateEmail, trimFormData } from '../utils/errorHandler';
import './Auth.css';

interface LoginProps {
  onLoginSuccess?: () => void;
}

const Login: React.FC<LoginProps> = ({ onLoginSuccess }) => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    rememberMe: false
  });
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [error, setError] = useState('');
  const [showResend, setShowResend] = useState(false);
  const [resendMessage, setResendMessage] = useState('');
  const navigate = useNavigate();
  const { showSuccess, showError } = useNotifications();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    // Trim and validate
    const trimmedData = trimFormData(formData);
    
    if (!validateEmail(trimmedData.email)) {
      setError('Please enter a valid email address');
      setLoading(false);
      return;
    }

    try {
      const loginData: LoginRequest = {
        email: trimmedData.email,
        password: trimmedData.password,
        rememberMe: trimmedData.rememberMe
      };

      await apiService.login(loginData);
      showSuccess('Login Successful', 'Welcome back!');
      onLoginSuccess?.();
      navigate('/app');
    } catch (err: any) {
      const appError = handleApiError(err);
      setError(appError.message);
      
      // Check for specific error codes to show resend option
      if (appError.code === 'EMAIL_NOT_VERIFIED') {
        setShowResend(true);
      } else {
        setShowResend(false);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResendVerification = async () => {
    if (!formData.email) {
      setError('Please enter your email address first');
      return;
    }

    setResending(true);
    setResendMessage('');
    setError('');

    try {
      const response = await apiService.resendVerification(formData.email.trim());
      setResendMessage(response.message || 'Verification email sent! Please check your inbox.');
      showSuccess('Email Sent', 'Verification email sent! Please check your inbox.');
      setShowResend(false);
    } catch (err: any) {
      const appError = handleApiError(err);
      setError(appError.message);
      showError('Email Error', appError.message);
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-card">
        <div className="auth-header">
          <div className="auth-logo">
            <span className="logo-icon">💰</span>
            <h1>RokdaHisab</h1>
          </div>
          <h2>Welcome Back</h2>
          <p>Sign in to your account to continue</p>
        </div>

        <form onSubmit={handleSubmit} className="auth-form">
          {error && (
            <div className="error-message">
              <span className="error-icon">⚠️</span>
              {error}
            </div>
          )}

          {resendMessage && (
            <div className="success-message">
              <span className="success-icon">✅</span>
              {resendMessage}
            </div>
          )}

          {showResend && (
            <div style={{ 
              padding: '15px', 
              backgroundColor: '#f0f7ff', 
              borderRadius: '8px', 
              marginBottom: '20px',
              border: '1px solid #4CAF50'
            }}>
              <p style={{ margin: '0 0 10px 0', color: '#333' }}>
                Please verify your email before logging in.
              </p>
              <button
                type="button"
                onClick={handleResendVerification}
                disabled={resending}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#4CAF50',
                  color: 'white',
                  border: 'none',
                  borderRadius: '5px',
                  cursor: resending ? 'not-allowed' : 'pointer',
                  fontSize: '14px'
                }}
              >
                {resending ? 'Sending...' : 'Resend verification email'}
              </button>
            </div>
          )}

          <div className="form-group">
            <label htmlFor="email">Email Address</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
              placeholder="Enter your email"
              className="form-input"
            />
          </div>

          <div className="form-group">
            <label htmlFor="password">Password</label>
            <input
              type="password"
              id="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              required
              placeholder="Enter your password"
              className="form-input"
            />
          </div>

          <div className="form-options">
            <label className="checkbox-label">
              <input 
                type="checkbox" 
                name="rememberMe"
                checked={formData.rememberMe}
                onChange={handleChange}
              />
              <span className="checkmark"></span>
              Remember me
            </label>
            <Link to="/forgot-password" className="forgot-link">
              Forgot password?
            </Link>
          </div>

          <button 
            type="submit" 
            className="auth-button"
            disabled={loading}
          >
            {loading ? (
              <>
                <span className="spinner"></span>
                Signing in...
              </>
            ) : (
              <>
                <span>Sign In</span>
                <span className="button-icon">→</span>
              </>
            )}
          </button>
        </form>

        <div className="auth-footer">
          <p>
            Don't have an account?{' '}
            <Link to="/register" className="auth-link">
              Sign up here
            </Link>
          </p>
        </div>
      </div>

      <div className="auth-background">
        <div className="floating-shapes">
          <div className="shape shape-1">💳</div>
          <div className="shape shape-2">📊</div>
          <div className="shape shape-3">💰</div>
        </div>
      </div>
    </div>
  );
};

export default Login;
